import { Button, Flex, Pagination, Paper, Tooltip } from '@mantine/core'
import { useInterval } from '@mantine/hooks'
import { IconPlus, IconRefresh } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetVlmPipelineList } from '@/queries/vlmQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { Pipeline } from '@/types/vlm'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { useFeatureUsage } from '../subscriptions/useFeatureUsage'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { VlmPipelineCreateModal } from './VlmPipelineCreateModal'
import { VlmPipelineDeleteModal } from './VlmPipelineDeleteModal'
import { VlmPipelineList } from './VlmPipelineList'
import { VlmPipelineRestartModal } from './VlmPipelineRestartModal'
import { VlmPipelineForm } from './pipeline-create/VlmPipelineForm'

const PAGE_SIZE = 10
const POLL_INTERVAL = 20000

// Shared state for create (pipeline: null) and edit (pipeline: Pipeline) modal
type PipelineFormModal = {
  open: boolean
  pipeline: Pipeline | null
}

export const VlmPipelinesPage = () => {
  const [canCreatePipeline] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Create
  ])

  const { usage: vlmAgentsUsage } = useFeatureUsage('vlm_agents')
  const hasVlmAgentsAccess = !!vlmAgentsUsage && vlmAgentsUsage.limit > 0
  const canCreateVlmPipeline = !!vlmAgentsUsage && vlmAgentsUsage.remaining > 0

  const [tableState, setTableState] = useTableState({ activePage: 1 })
  const { activePage } = tableState

  const [pipelineFormModal, setPipelineFormModal] = useState<PipelineFormModal>(
    { open: false, pipeline: null }
  )
  const [pipelineToDelete, setPipelineToDelete] = useState<Pipeline | null>(
    null
  )
  const [pipelineToRestart, setPipelineToRestart] = useState<Pipeline | null>(
    null
  )

  const { data, isLoading, isFetching, isError, refetch } =
    useGetVlmPipelineList(
      {
        offset: getPageOffset(activePage, PAGE_SIZE),
        limit: PAGE_SIZE,
        ordering: 'created_at'
      },
      { keepPreviousData: true }
    )

  const pipelines = data?.results ?? []
  const totalPageCount = getPageCount(data?.count ?? 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const { start, stop, active } = useInterval(
    () => void refetch(),
    POLL_INTERVAL
  )

  useEffect(() => {
    if (data && !active) {
      start()
    }
  }, [data, active, start])

  useEffect(() => {
    return stop
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const openPipelineForm = (pipeline: Pipeline | null = null) =>
    setPipelineFormModal({ open: true, pipeline })

  const closePipelineForm = () =>
    setPipelineFormModal({ open: false, pipeline: null })

  return (
    <>
      <Paper p="sm" mb="md" bg="gray.0" withBorder>
        <Flex justify="space-between" align="center">
          {canCreatePipeline && (
            <Tooltip
              label={
                <FormattedMessage
                  id={
                    hasVlmAgentsAccess
                      ? 'vlm.capacity.disabledTooltip'
                      : 'vlm.entitlement.disabledTooltip'
                  }
                />
              }
              disabled={canCreateVlmPipeline}
            >
              <Button
                leftSection={<IconPlus size={16} />}
                disabled={!canCreateVlmPipeline}
                onClick={() => openPipelineForm()}
              >
                <FormattedMessage id="vlm.pipelines.actions.create" />
              </Button>
            </Tooltip>
          )}

          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={isFetching}
            onClick={() => void refetch()}
          >
            <FormattedMessage id="refresh" />
          </Button>
        </Flex>
      </Paper>

      <VlmPipelineList
        pipelines={pipelines}
        isLoading={isLoading || isFetching}
        hasError={isError}
        hasVlmAgentsAccess={hasVlmAgentsAccess}
        onReload={() => void refetch()}
        onEdit={openPipelineForm}
        onDelete={setPipelineToDelete}
        onRestart={setPipelineToRestart}
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      <VlmPipelineCreateModal
        opened={pipelineFormModal.open}
        isEditing={pipelineFormModal.pipeline !== null}
        onClose={closePipelineForm}
      >
        <VlmPipelineForm
          pipeline={pipelineFormModal.pipeline ?? undefined}
          onClose={closePipelineForm}
        />
      </VlmPipelineCreateModal>

      <VlmPipelineDeleteModal
        opened={pipelineToDelete !== null}
        pipeline={pipelineToDelete}
        onClose={() => setPipelineToDelete(null)}
      />

      <VlmPipelineRestartModal
        opened={pipelineToRestart !== null}
        pipeline={pipelineToRestart}
        onClose={() => setPipelineToRestart(null)}
      />
    </>
  )
}
